import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import {
  DocInterpretationAvailability,
  DocInterpretationConflictChoice,
  DocInterpretationEvent,
  DocInterpretationItemMap,
  DocInterpretationLogPage,
  DocInterpretationOrderLineHit,
  DocInterpretationItemHit,
  DocInterpretationOrderHit,
  DocInterpretationOverview,
  DocInterpretationQuantityChoice,
  DocInterpretationReceiptLine,
  DocInterpretationResult,
  DocInterpretationSettings,
  DocInterpretationUpload,
} from '../types/docInterpretation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Dokumenttolking i varemottak (TW 36422795): opplasting, tolking og avgjørelser per linje.
 * Backend: DocInterpretationController (api/v1/doc-interpretation).
 */
class DocInterpretationService {
  private readonly baseUrl = `${API_BASE_URL}/doc-interpretation`;

  private async parse<T>(response: Response, fallback: string): Promise<T> {
    const text = await response.text();
    // En HTML-feilside (f.eks. 502 fra IIS) skal gi feilmeldingen, ikke «Unexpected token <»
    let data: unknown = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = null; }
    if (!response.ok) {
      throw new Error(extractApiError(data, response, fallback));
    }
    return data as T;
  }

  /** Om funksjonen finnes for tenanten (migrasjon 127) og er aktivert. Styrer menyer og knapper. */
  async getAvailability(): Promise<DocInterpretationAvailability> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/availability`);
    return this.parse(response, 'Kunne ikke hente status for dokumenttolking');
  }

  async getSettings(): Promise<DocInterpretationSettings> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/settings`);
    return this.parse(response, 'Kunne ikke hente oppsettet for dokumenttolking');
  }

  /** Lagrer oppsettet. Aktivering, tak og AI-modell kan bare endres av IntegrasjonsPartner. */
  async saveSettings(settings: DocInterpretationSettings): Promise<DocInterpretationSettings> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/settings`, settings);
    return this.parse(response, 'Kunne ikke lagre oppsettet');
  }

  /** Forbruk mot tak, fordeling skript/AI, leverandører og malstatus, historikk (punkt 29). */
  async getOverview(): Promise<DocInterpretationOverview> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/overview`);
    return this.parse(response, 'Kunne ikke hente oversikten');
  }

  /** Loggen «Tolkede dokumenter». */
  async getLog(filter: { status?: string; supplierNo?: string; limit?: number; offset?: number }): Promise<DocInterpretationLogPage> {
    const params = new URLSearchParams();
    if (filter.status) params.append('status', filter.status);
    if (filter.supplierNo) params.append('supplierNo', filter.supplierNo);
    params.append('limit', String(filter.limit ?? 100));
    params.append('offset', String(filter.offset ?? 0));
    const response = await AuthService.authenticatedGet(`${this.baseUrl}?${params.toString()}`);
    return this.parse(response, 'Kunne ikke hente loggen');
  }

  async getEvents(id: number): Promise<DocInterpretationEvent[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${id}/events`);
    return this.parse(response, 'Kunne ikke hente valgene');
  }

  async getItemMaps(supplierNo?: string): Promise<DocInterpretationItemMap[]> {
    const query = supplierNo ? `?supplierNo=${encodeURIComponent(supplierNo)}` : '';
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/item-maps${query}`);
    return this.parse(response, 'Kunne ikke hente koblingene');
  }

  async deleteItemMap(id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/item-maps/${id}`);
    if (!response.ok) {
      throw new Error(extractApiError(null, response, 'Kunne ikke fjerne koblingen'));
    }
  }

  /** Laster opp og tolker et leverandørdokument (punkt 4–6). */
  async interpret(upload: DocInterpretationUpload): Promise<DocInterpretationResult> {
    const formData = new FormData();
    formData.append('file', upload.file);
    if (upload.documentType) formData.append('documentType', upload.documentType);
    if (upload.orderNos.length > 0) formData.append('orderNos', upload.orderNos.join(','));
    if (upload.reinterpret) formData.append('reinterpret', 'true');

    const response = await AuthService.authenticatedFetch(this.baseUrl, { method: 'POST', body: formData });
    return this.parse(response, 'Kunne ikke tolke dokumentet');
  }

  async get(id: number): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${id}`);
    return this.parse(response, 'Kunne ikke hente tolkingen');
  }

  /** Originaldokumentet som Blob, for visning ved siden av linjene (punkt 15). */
  async getFile(id: number): Promise<Blob> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${id}/file`);
    if (!response.ok) {
      throw new Error(extractApiError(null, response, 'Kunne ikke hente dokumentet'));
    }
    return response.blob();
  }

  async correctSupplier(id: number, supplierNo: string): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}/supplier`, { supplierNo });
    return this.parse(response, 'Kunne ikke rette leverandøren');
  }

  async searchOrderLines(id: number, query: string): Promise<DocInterpretationOrderLineHit[]> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/${id}/order-lines?q=${encodeURIComponent(query)}`
    );
    return this.parse(response, 'Søket feilet');
  }

  /** «Ny bestillingslinje»: linjen opprettes på bestillingen ved OK, med varen som er valgt. */
  async createOrderLine(id: number, lineId: number, orderNo: number, itemNo: string): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/${id}/lines/${lineId}/order-line`, { orderNo, itemNo });
    return this.parse(response, 'Kunne ikke lagre valget');
  }

  /** Åpne bestillinger fra leverandøren, til «Ny bestillingslinje». */
  async searchOrders(id: number, term: string): Promise<DocInterpretationOrderHit[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${id}/orders?term=${encodeURIComponent(term)}`);
    return this.parse(response, 'Søket feilet');
  }

  /** Varer i Compass, til «Ny bestillingslinje». */
  async searchItems(id: number, term: string): Promise<DocInterpretationItemHit[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${id}/items?term=${encodeURIComponent(term)}`);
    return this.parse(response, 'Søket feilet');
  }

  async linkLine(id: number, lineId: number, orderNo: number, lineNo: number): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}/lines/${lineId}/link`, { orderNo, lineNo });
    return this.parse(response, 'Kunne ikke koble linjen');
  }

  async chooseQuantity(
    id: number,
    lineId: number,
    choice: DocInterpretationQuantityChoice,
    quantity?: number
  ): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}/lines/${lineId}/quantity`, {
      choice,
      quantity: quantity ?? null,
    });
    return this.parse(response, 'Kunne ikke lagre antallet');
  }

  async chooseConflict(id: number, lineId: number, choice: DocInterpretationConflictChoice): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}/lines/${lineId}/conflict`, { choice });
    return this.parse(response, 'Kunne ikke lagre valget');
  }

  /** Retter antall og batch per mottakslinje (punkt 19). Tom liste = tilbake til standard. */
  async setReceiptLines(id: number, lineId: number, receiptLines: DocInterpretationReceiptLine[]): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}/lines/${lineId}/receipt-lines`, { receiptLines });
    return this.parse(response, 'Kunne ikke lagre mottakslinjene');
  }

  async setExcluded(id: number, lineId: number, excluded: boolean): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}/lines/${lineId}/excluded`, { excluded });
    return this.parse(response, 'Kunne ikke lagre valget');
  }

  /**
   * OK (TW 36423839): skriver dokumentnummeret på linjene og ankomstregistrerer etter oppsettet.
   * arrivalRegistration overstyrer «Ankomstregistrer som standard» for dokumentet. Endelig — kan ikke angres.
   */
  async approve(id: number, arrivalRegistration: boolean): Promise<DocInterpretationResult> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/${id}/approve`, { arrivalRegistration });
    return this.parse(response, 'Kunne ikke godkjenne dokumentet');
  }
}

export const docInterpretationService = new DocInterpretationService();
